import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sprout } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FarmAnalysisCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/finance/farm-analysis`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const profitPositive = data.farm_profit >= 0;

  return (
    <Card data-testid="farm-analysis-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Sprout className="h-4 w-4 text-green-600" /> School Farm Financial Analysis
        </CardTitle>
        <CardDescription>Farm income vs expenses — profit or loss at a glance</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg p-3 bg-green-50 dark:bg-green-900/15">
            <p className="text-xs text-muted-foreground">Farm Income</p>
            <p className="text-lg font-heading font-bold text-green-600" data-testid="farm-income">{formatCurrency(data.farm_income)}</p>
            <p className="text-[10px] text-muted-foreground">{data.record_counts.income} record(s)</p>
          </div>
          <div className="rounded-lg p-3 bg-red-50 dark:bg-red-900/15">
            <p className="text-xs text-muted-foreground">Farm Expenses</p>
            <p className="text-lg font-heading font-bold text-red-600" data-testid="farm-expenses">{formatCurrency(data.farm_expenses)}</p>
            <p className="text-[10px] text-muted-foreground">{data.record_counts.expenses} record(s)</p>
          </div>
          <div className={`rounded-lg p-3 ${profitPositive ? 'bg-emerald-50 dark:bg-emerald-900/15' : 'bg-orange-50 dark:bg-orange-900/15'}`}>
            <p className="text-xs text-muted-foreground">{profitPositive ? 'Farm Profit' : 'Farm Loss'}</p>
            <p className={`text-lg font-heading font-bold ${profitPositive ? 'text-emerald-600' : 'text-orange-600'}`} data-testid="farm-profit">
              {formatCurrency(data.farm_profit)}
            </p>
          </div>
        </div>
        {data.monthly_series.length > 0 && (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data.monthly_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Legend />
              <Bar dataKey="income" name="Farm Income" fill="#22C55E" radius={[3, 3, 0, 0]} />
              <Bar dataKey="expenses" name="Farm Expenses" fill="#EF4444" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
